import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AppApis } from '@app/core/APIs/app-apis';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { MatDialog, MatDialogModule, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { UiDialogComponent } from '@template-ui/components';
import { EzDialogData } from '@template-ui/types';
import { WorkflowRecordGridComponent, WorkflowRecordColumn } from '@app/shared/workflow-blocks/workflow-record-grid.component';

export interface EligibilityTestReadiness {
  ready: boolean;
  eligibilityDate: string;
  blockers: {
    code: string;
    label: string;
    message: string;
    owner: string;
    actionUrl: string | null;
  }[];
}
export interface EligibilityTestResult {
  runId: string;
  definitionVersionId: number;
  workflowCode: string;
  versionCode: string;
  eligibilityDate: string;
  status: string;
  candidateCount: number;
  processedCount: number;
  issueCode: string | null;
  createdUtc: string;
  counts: Record<string, number>;
  candidates: {
    ordinal: number;
    personId: number;
    status: string;
    issueCode: string | null;
    workflowInstanceId: number | null;
    publicReference: string | null;
    inmateName?: string | null;
    inmateNumber?: string | null;
    institutionName?: string | null;
  }[];
}
interface EligibilityTestView {
  readonly context: string;
  readonly lease: number;
}

type TestRecord = EligibilityTestResult['candidates'][number] & { outcome: string; explanation: string };

@Component({
  selector: 'phms-hearing-eligibility-test',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    UiButtonDirective,
    UiControlDirective,
    WorkflowRecordGridComponent,
    MatDialogModule,
  ],
  templateUrl: './hearing-eligibility-test.component.html',
  styleUrl: './hearing-eligibility-test.component.scss',
})
export class HearingEligibilityTestComponent implements OnChanges, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly apis = inject(AppApis);
  private readonly destroy = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private static nextId = 0;
  readonly idPrefix = `hearing-eligibility-test-${++HearingEligibilityTestComponent.nextId}`;
  private modalRef: MatDialogRef<UiDialogComponent> | null = null;
  private modalClosing = false;
  private modalContext = '';
  private modalBackground: HTMLElement | null = null;
  private returnOrigin: HTMLElement | null = null;
  private focusRequest = 0;
  private readLease = 0;
  private checkRequest = 0;
  private historyRequest = 0;
  private resultRequest = 0;
  private readinessWitness: {
    context: string;
    date: string;
    lease: number;
  } | null = null;
  private routeRunId: string | null = null;
  private restoringDate = false;
  @ViewChild('testBody', { static: true })
  private testBody!: TemplateRef<unknown>;
  private detailsDialog?: MatDialogRef<unknown>;
  @ViewChild('workflowCell', { static: true }) workflowCell!: TemplateRef<unknown>;
  @ViewChild('detailsCell', { static: true }) detailsCell!: TemplateRef<unknown>;
  @ViewChild('recordDetails', { static: true }) recordDetails!: TemplateRef<unknown>;
  selectedRecord: TestRecord | null = null;
  recordRows: TestRecord[] = [];
  private configuredColumns?: WorkflowRecordColumn[];
  @Input({ required: true }) definitionVersionId = 0;
  @Input({ required: true }) versionCode = '';
  @Input() interactionBlocked = false;
  @Input() autoOpen = false;
  @Input() presentation: 'inline' | 'modal' = 'inline';
  @Input() sourceContext = '';
  @Input() currentSourceContext: (() => string | null) | null = null;
  opened = false;
  checking = false;
  starting = false;
  refreshing = false;
  error = '';
  readiness: EligibilityTestReadiness | null = null;
  private currentResult: EligibilityTestResult | null = null;
  get result(): EligibilityTestResult | null { return this.currentResult; }
  set result(value: EligibilityTestResult | null) {
    this.closeDetails();
    this.currentResult = value;
    this.recordRows = value?.candidates.map(candidate => ({ ...candidate,
      outcome: this.statusLabel(candidate.status), explanation: this.outcomeExplanation(candidate),
    })) ?? [];
  }
  get columns(): WorkflowRecordColumn[] {
    return this.configuredColumns ??= [
      { key: 'inmateName', label: 'Inmate name', minWidth: 180 },
      { key: 'inmateNumber', label: 'Inmate number', minWidth: 140 },
      { key: 'institutionName', label: 'Location', minWidth: 150 },
      { key: 'outcome', label: 'Outcome', minWidth: 220 },
      { key: 'publicReference', label: 'Hearing workflow', minWidth: 140, template: this.workflowCell },
      { key: 'actions', label: 'Actions', minWidth: 100, template: this.detailsCell },
    ];
  }
  history: EligibilityTestResult[] = [];
  eligibilityDate = new Date().toISOString().slice(0, 10);
  private generation = 0;
  private requestId: string | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly options = {
    withCredentials: true,
    headers: new HttpHeaders({
      'skip-error-interceptor': 'yes',
      'skip-loading-interceptor': 'yes',
    }),
  };
  private get url(): string {
    return this.apis.core.manual.getHearingEligibilityTests();
  }
  get running(): boolean {
    return (
      this.result?.status === 'pending' || this.result?.status === 'running'
    );
  }
  readonly categories = [
    { key: 'enrolled', label: 'New hearing workflows' },
    { key: 'ineligible', label: 'Not eligible' },
    { key: 'missing-facts', label: 'Missing information' },
    { key: 'unresolved-policy', label: 'Policy needs attention' },
    { key: 'technical-failure', label: 'Could not process' },
  ];
  get testView(): EligibilityTestView {
    return { context: this.sourceKey(), lease: this.readLease };
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['definitionVersionId'] &&
      !changes['versionCode'] &&
      !changes['sourceContext'] &&
      !changes['presentation']
    ) {
      if (
        this.presentation === 'inline' &&
        this.autoOpen &&
        !this.interactionBlocked &&
        this.definitionVersionId > 0 &&
        !this.opened &&
        !this.readiness &&
        !this.result &&
        !this.checking &&
        (changes['autoOpen'] || changes['interactionBlocked'])
      )
        void this.open();
      return;
    }
    this.generation++;
    this.closeModal(true);
    this.stopPolling();
    this.opened = false;
    this.starting = false;
    this.checking = false;
    this.refreshing = false;
    this.result = null;
    this.readiness = null;
    this.readinessWitness = null;
    this.history = [];
    this.error = '';
    this.requestId = null;
    const run = this.route.snapshot.queryParamMap.get('eligibilityTest');
    this.routeRunId = run && /^[0-9a-f-]{36}$/i.test(run) ? run : null;
    if (this.presentation === 'inline') {
      if (this.routeRunId) {
        this.opened = true;
        void this.resume(this.routeRunId);
      } else if (this.autoOpen && this.definitionVersionId > 0)
        void this.open();
    }
  }
  ngOnDestroy(): void {
    this.generation++;
    this.focusRequest++;
    this.closeModal(true);
    this.stopPolling();
    this.closeDetails();
  }
  openDetails(record: TestRecord, view?: EligibilityTestView): void {
    if (!this.viewCurrent(view) || !this.recordRows.includes(record)) return;
    this.closeDetails();
    this.selectedRecord = record;
    this.detailsDialog = this.dialog.open(this.recordDetails, {
      width: '560px',
      maxWidth: '95vw',
      ariaLabel: 'Eligibility test record details',
    });
  }
  closeDetails(): void {
    this.detailsDialog?.close();
    this.detailsDialog = undefined;
    this.selectedRecord = null;
  }
  private outcomeExplanation(candidate: EligibilityTestResult['candidates'][number]): string {
    if (candidate.issueCode === 'synthetic.injected-processing-failure') return 'Intentional processing failure for this test case.';
    return ({ pending: 'Waiting to be processed.', running: 'Eligibility is being evaluated.',
      enrolled: 'Hearing workflow created for this version.', 'existing-enrollment': 'An existing hearing workflow was retained.',
      ineligible: 'Initial-hearing eligibility rules were not met.', 'missing-facts': 'Required eligibility information is incomplete.',
      'unresolved-policy': 'An applicable hearing policy could not be resolved.', 'technical-failure': 'Processing could not complete. Review the record details.',
    } as Record<string, string>)[candidate.status] ?? 'Review the record details.';
  }
  async open(origin?: HTMLElement): Promise<void> {
    if (this.interactionBlocked || !this.sourceCurrent()) return;
    if (this.presentation === 'modal') {
      if (
        this.modalClosing ||
        !this.hostVisible() ||
        (!this.modalRef &&
          this.element.nativeElement.closest('[inert],[aria-hidden="true"]')) ||
        this.dialog.openDialogs.some((ref) => ref !== this.modalRef)
      )
        return;
      if (!this.modalRef) this.showModal(origin);
      if (!this.modalRef || this.modalRef.getState() !== MatDialogState.OPEN)
        return;
    }
    this.opened = true;
    if (this.result) {
      if (this.presentation === 'modal')
        await this.resume(this.result.runId, true);
      return;
    }
    if (this.presentation === 'modal' && this.routeRunId) {
      await this.resume(this.routeRunId);
      return;
    }
    await Promise.all([this.check(), this.loadHistory()]);
  }
  close(view?: EligibilityTestView): void {
    if (
      view &&
      (this.presentation === 'modal'
        ? !this.modalRef ||
          this.modalClosing ||
          view.context !== this.modalContext ||
          view.context !== this.sourceKey() ||
          view.lease !== this.readLease
        : !this.viewCurrent(view))
    )
      return;
    if (this.presentation === 'modal') this.closeModal(true);
    else this.opened = false;
  }

  actionLink(url: string): UrlTree { return this.router.parseUrl(url); }
  async changedDate(
    view?: EligibilityTestView,
    value?: string,
    model?: NgModel,
  ): Promise<void> {
    if (this.restoringDate) return;
    if (!this.viewCurrent(view) || this.starting || this.running) {
      this.restoreDate(model);
      return;
    }
    if (value !== undefined) this.eligibilityDate = value;
    this.requestId = null;
    await this.check(view);
  }
  async check(view?: EligibilityTestView): Promise<void> {
    if (!this.viewCurrent(view) || this.starting) return;
    const generation = this.generation,
      request = ++this.checkRequest,
      context = this.sourceKey(),
      lease = this.readLease,
      date = this.eligibilityDate;
    this.checking = true;
    this.readiness = null;
    this.readinessWitness = null;
    this.error = '';
    if (!date) {
      this.checking = false;
      return;
    }
    try {
      const report = await firstValueFrom(
        this.http
          .get<EligibilityTestReadiness>(this.url + '/readiness', {
            ...this.options,
            params: {
              definitionVersionId: this.definitionVersionId,
              eligibilityDate: date,
            },
          })
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      if (
        !this.readCurrent(generation, context, lease) ||
        request !== this.checkRequest
      )
        return;
      if (this.presentation === 'modal' && report.eligibilityDate !== date) {
        this.error =
          'Readiness does not match the selected eligibility date. Check again.';
        return;
      }
      this.readiness = report;
      this.readinessWitness = { context, date, lease };
    } catch (error) {
      if (
        this.readCurrent(generation, context, lease) &&
        request === this.checkRequest
      )
        this.error = this.message(error);
    } finally {
      if (
        this.requestOwned(generation, context, lease) &&
        request === this.checkRequest
      )
        this.checking = false;
    }
  }
  private async loadHistory(): Promise<void> {
    const generation = this.generation,
      context = this.sourceKey(),
      lease = this.readLease,
      request = ++this.historyRequest,
      definition = this.definitionVersionId;
    try {
      const rows = await firstValueFrom(
        this.http
          .get<EligibilityTestResult[]>(this.url, {
            ...this.options,
            params: { definitionVersionId: definition },
          })
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      if (
        this.readCurrent(generation, context, lease) &&
        request === this.historyRequest
      )
        this.history = rows.filter(
          (row) =>
            row.definitionVersionId === definition &&
            (this.presentation === 'inline' ||
              row.versionCode === this.versionCode),
        );
    } catch (error) {
      if (
        this.readCurrent(generation, context, lease) &&
        request === this.historyRequest
      )
        this.error = this.message(error);
    }
  }
  async start(view?: EligibilityTestView): Promise<void> {
    if (
      this.starting ||
      this.running ||
      this.checking ||
      !this.readiness?.ready ||
      this.interactionBlocked ||
      !this.viewCurrent(view)
    )
      return;
    const context = this.sourceKey(),
      witness = this.readinessWitness;
    if (
      this.presentation === 'modal' &&
      (!witness ||
        witness.context !== context ||
        witness.date !== this.eligibilityDate ||
        witness.lease !== this.readLease)
    )
      return;
    const generation = this.generation,
      definition = this.definitionVersionId,
      date = this.eligibilityDate;
    this.starting = true;
    this.error = '';
    this.requestId ??= crypto.randomUUID();
    try {
      const result = await firstValueFrom(
        this.http
          .post<EligibilityTestResult>(
            this.url,
            {
              requestId: this.requestId,
              definitionVersionId: definition,
              eligibilityDate: date,
            },
            this.options,
          )
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      if (
        generation !== this.generation ||
        context !== this.sourceKey() ||
        !this.sourceCurrent()
      )
        return;
      if (
        result.definitionVersionId !== definition ||
        (this.presentation === 'modal' &&
          (result.eligibilityDate !== date ||
            result.versionCode !== this.versionCode))
      ) {
        this.error =
          'The test response did not match this publication and eligibility date. Retry to check the same request.';
        return;
      }
      this.result = result;
      this.routeRunId = result.runId;
      if (this.presentation === 'inline' || this.opened) {
        this.writeRunQuery(result.runId);
        this.poll();
      }
    } catch (error) {
      if (
        generation === this.generation &&
        context === this.sourceKey() &&
        this.sourceCurrent()
      )
        this.error = this.message(error);
    } finally {
      if (generation === this.generation && context === this.sourceKey())
        this.starting = false;
    }
  }
  async resume(
    runId: string,
    background = false,
    view?: EligibilityTestView,
  ): Promise<void> {
    if (!this.viewCurrent(view)) return;
    const generation = this.generation,
      context = this.sourceKey(),
      lease = this.readLease,
      request = ++this.resultRequest;
    if (!background) this.opened = true;
    this.refreshing = true;
    this.error = '';
    try {
      const result = await firstValueFrom(
        this.http
          .get<EligibilityTestResult>(this.url + '/' + runId, this.options)
          .pipe(takeUntilDestroyed(this.destroy)),
      );
      if (
        !this.readCurrent(generation, context, lease) ||
        request !== this.resultRequest
      )
        return;
      if (
        result.definitionVersionId !== this.definitionVersionId ||
        result.runId !== runId ||
        (this.presentation === 'modal' &&
          result.versionCode !== this.versionCode)
      ) {
        if (this.presentation === 'modal')
          this.error =
            'This test belongs to another publication. Reopen a recent test of this version.';
        else this.opened = false;
        return;
      }
      this.result = result;
      this.routeRunId = result.runId;
      this.poll();
      if (!background) this.writeRunQuery(result.runId);
    } catch (error) {
      if (
        this.readCurrent(generation, context, lease) &&
        request === this.resultRequest
      )
        this.error = this.message(error);
    } finally {
      if (
        this.requestOwned(generation, context, lease) &&
        request === this.resultRequest
      )
        this.refreshing = false;
    }
  }
  async freshTest(view?: EligibilityTestView): Promise<void> {
    if (
      this.running ||
      this.starting ||
      this.interactionBlocked ||
      !this.viewCurrent(view)
    )
      return;
    this.result = null;
    this.requestId = null;
    this.routeRunId = null;
    this.writeRunQuery(null);
    await this.open();
  }
  private writeRunQuery(runId: string | null): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { eligibilityTest: runId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
  private poll(): void {
    this.stopPolling();
    if (
      this.running &&
      this.result &&
      (this.presentation === 'inline' || this.viewCurrent())
    ) {
      const id = this.result.runId,
        generation = this.generation,
        context = this.sourceKey(),
        lease = this.readLease;
      this.timer = setTimeout(() => {
        if (
          this.readCurrent(generation, context, lease) &&
          this.result?.runId === id
        )
          void this.resume(id, true);
      }, 2000);
    }
  }
  private stopPolling(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
  }
  private sourceKey(): string {
    return JSON.stringify([
      this.definitionVersionId,
      this.versionCode,
      this.sourceContext,
      this.presentation,
    ]);
  }
  private sourceCurrent(): boolean {
    return (
      !this.destroy.destroyed &&
      (this.presentation === 'inline' ||
        (this.definitionVersionId > 0 &&
          (!this.currentSourceContext ||
            this.currentSourceContext() === this.sourceContext)))
    );
  }
  private hostVisible(): boolean {
    const host = this.element.nativeElement;
    if (!host.isConnected || host.closest('[hidden]')) return false;
    for (let node: HTMLElement | null = host; node; node = node.parentElement) {
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return false;
    }
    return true;
  }
  private viewCurrent(view?: EligibilityTestView): boolean {
    if (this.presentation === 'inline') return !this.destroy.destroyed;
    return (
      this.sourceCurrent() &&
      this.hostVisible() &&
      this.backgroundCurrent() &&
      this.opened &&
      !!this.modalRef &&
      this.modalRef.getState() === MatDialogState.OPEN &&
      !this.modalClosing &&
      this.modalContext === this.sourceKey() &&
      (!view ||
        (view.context === this.modalContext &&
          view.lease === this.readLease)) &&
      this.dialog.openDialogs.every(
        (ref) => ref === this.modalRef || ref === this.detailsDialog,
      )
    );
  }
  private backgroundCurrent(): boolean {
    const host = this.element.nativeElement;
    for (let node: HTMLElement | null = host; node; node = node.parentElement) {
      if (node.hasAttribute('inert')) return false;
      if (
        node.getAttribute('aria-hidden') === 'true' &&
        (node !== this.modalBackground ||
          node.parentElement !== host.ownerDocument.body ||
          !this.modalRef ||
          !this.dialog.openDialogs.includes(this.modalRef))
      )
        return false;
    }
    return true;
  }
  private requestOwned(
    generation: number,
    context: string,
    lease: number,
  ): boolean {
    return (
      generation === this.generation &&
      context === this.sourceKey() &&
      (this.presentation === 'inline' || lease === this.readLease)
    );
  }
  private readCurrent(
    generation: number,
    context: string,
    lease: number,
  ): boolean {
    return (
      this.requestOwned(generation, context, lease) &&
      this.sourceCurrent() &&
      (this.presentation === 'inline' || this.viewCurrent())
    );
  }
  private restoreDate(model?: NgModel): void {
    if (
      !model ||
      (model.control.value === this.eligibilityDate &&
        model.viewModel === this.eligibilityDate)
    )
      return;
    this.restoringDate = true;
    try {
      model.control.setValue(this.eligibilityDate, { emitEvent: false });
    } finally {
      this.restoringDate = false;
    }
  }
  private showModal(origin?: HTMLElement): void {
    this.focusRequest++;
    this.modalClosing = false;
    this.modalContext = this.sourceKey();
    this.readLease++;
    const host = this.element.nativeElement,
      doc = host.ownerDocument;
    this.modalBackground = null;
    for (
      let node: HTMLElement | null = host;
      node && node !== doc.body;
      node = node.parentElement
    )
      if (node.parentElement === doc.body) this.modalBackground = node;
    const active = doc.activeElement;
    this.returnOrigin =
      origin ?? (active instanceof HTMLElement ? active : null);
    this.opened = true;
    const ref = this.dialog.open(UiDialogComponent, {
      width: '64rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100dvh - 2rem)',
      autoFocus: `#${this.idPrefix}-panel`,
      restoreFocus: false,
      disableClose: false,
      ariaLabel: `Test version ${this.versionCode}`,
      data: {
        heading: `Test version ${this.versionCode}`,
        buttons: [],
      } satisfies EzDialogData,
    });
    this.modalRef = ref;
    ref.componentRef?.setInput('contentTemplate', this.testBody);
    ref.beforeClosed().subscribe(() => this.closeModal(false));
    ref.afterClosed().subscribe(() => {
      if (this.modalRef !== ref) return;
      const origin = this.returnOrigin,
        context = this.modalContext;
      this.modalRef = null;
      this.returnOrigin = null;
      this.modalClosing = false;
      this.modalBackground = null;
      if (origin) this.restoreFocus(origin, context);
    });
  }
  private closeModal(requestClose: boolean): void {
    const ref = this.modalRef;
    if (ref && !this.modalClosing) {
      this.modalClosing = true;
      this.focusRequest++;
      this.readLease++;
      this.checkRequest++;
      this.historyRequest++;
      this.resultRequest++;
      this.opened = false;
      this.checking = false;
      this.refreshing = false;
      this.readiness = null;
      this.readinessWitness = null;
      this.stopPolling();
      this.closeDetails();
    }
    if (requestClose && ref) ref.close();
  }
  private restoreFocus(origin: HTMLElement, context: string): void {
    const request = ++this.focusRequest;
    setTimeout(() => {
      const doc = origin.ownerDocument,
        active = doc.activeElement;
      if (
        request !== this.focusRequest ||
        context !== this.sourceKey() ||
        !this.sourceCurrent() ||
        !this.hostVisible() ||
        this.modalRef ||
        this.dialog.openDialogs.length ||
        !origin.isConnected ||
        origin.closest('[hidden],[inert],[aria-hidden="true"]') ||
        origin.matches(':disabled') ||
        (active &&
          active !== doc.body &&
          active !== doc.documentElement &&
          active !== origin &&
          active.isConnected &&
          !active.closest('[hidden],[inert],[aria-hidden="true"]'))
      )
        return;
      origin.focus({ preventScroll: true });
    });
  }
  statusLabel(status: string): string {
    return (
      (
        {
          pending: 'Queued',
          running: 'Running',
          completed: 'Completed',
          completed_with_issues: 'Completed · review outcomes',
          blocked: 'Needs administrator attention',
          enrolled: 'Hearing workflow created',
          ineligible: 'Not eligible',
          'missing-facts': 'Missing information',
          'unresolved-policy': 'Policy needs attention',
          'technical-failure': 'Could not process',
          'existing-enrollment': 'Already has a hearing workflow',
        } as Record<string, string>
      )[status] ?? status
    );
  }
  private message(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401 || error.status === 403) {
        this.result = null;
        this.history = [];
        this.stopPolling();
        return 'Your current permissions do not allow this eligibility test.';
      }
      if (typeof error.error?.detail === 'string') return error.error.detail;
    }
    return 'The test could not be loaded. Your run is retained; use Refresh to check it again.';
  }
}
